import { SidebarProps } from '@/components/shared/Sidebar/Sidebar';
import SidebarLinkSet from '@/components/shared/Sidebar/SidebarLinkSet';
import { ApiProvider } from '@/provider/ApiProvider';
import {
  AddTask as ActivationIcon,
  BookmarkAdd as ActivityCreateIcon,
  LibraryAdd as BrandCreateIcon,
  NoteAdd as ContractCreateIcon,
  Add as PlusIcon,
  AddBusiness as PropertyCreateIcon,
  MoreHoriz as ThreeDotsIcon,
} from '@mui/icons-material';
import { Typography } from '@mui/material';
import Box from '@mui/system/Box';
import { FC, useMemo, useState } from 'react';
import SidebarLink, { SidebarLinkProps } from './SidebarLink';
import { basicLinkStyles } from './styles';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

interface AnchorEls {
  moreViews: HTMLElement | null;
  createNew: HTMLElement | null;
}

export interface HiddenVisibleLinksObj {
  visible: Array<SidebarLinkProps>;
  hidden: Array<SidebarLinkProps>;
}

export interface SidebarContentProps {
  links: HiddenVisibleLinksObj;
  compactSidebarMenus?: boolean;
}

const SidebarViews: FC<SidebarContentProps & SidebarProps> = ({
  links,
  property_id,
  brand_id,
  compactSidebarMenus = false,
}) => {
  const [anchorEls, setAnchorEls] = useState<AnchorEls>({ moreViews: null, createNew: null });

  const userCan = useMemo(() => {
    return {
      createContract: ApiProvider.permission('contract_create'),
      createActivation: ApiProvider.permission('activation_create'),
      createBrand: ApiProvider.permission('brand_tracker_create'),
      createProperty: ApiProvider.permission('property_tracker_create'),
    };
  }, []);

  const handleClick =
    (section: 'moreViews' | 'createNew') => (event: React.MouseEvent<HTMLElement>) => {
      const currentTarget = event.currentTarget;

      setAnchorEls(
        (prevAnchorEls: { moreViews: HTMLElement | null; createNew: HTMLElement | null }) => {
          const isAlreadyOpen = prevAnchorEls[section] === currentTarget;

          let newAnchorEls = { moreViews: null, createNew: null };

          if (!isAlreadyOpen) {
            newAnchorEls = { ...newAnchorEls, [section]: currentTarget };
          }

          return newAnchorEls;
        },
      );
    };

  const isSectionOpen = (section: keyof AnchorEls) => Boolean(anchorEls[section]);

  const viewsAddLinks = useMemo(
    () => [
      ...(userCan.createContract
        ? [
            {
              to: `/addcontract${property_id ? `/property/${property_id}` : brand_id ? `/brand/${brand_id}` : ''}`,
              icon: ContractCreateIcon,
              text: 'Contract',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
      ...(userCan.createProperty
        ? [
            {
              to: '/addproperty',
              icon: PropertyCreateIcon,
              text: 'Right Holder/Partner',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
      ...(Boolean(ApiProvider.permission('campaign_create'))
        ? [
            {
              to: '/addActivation',
              icon: ActivationIcon,
              text: 'Activation',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
      ...(Boolean(
        ApiProvider.permission('activation_create') &&
        ApiProvider.permission('activation_create_product_launch'),
      )
        ? [
            {
              to: '',
              icon: ActivityCreateIcon,
              text: 'Activity',
              boxContainerStyles: { ...basicLinkStyles },
            },
            {
              subMenu: true,
              to: '/addActivity',
              icon: KeyboardArrowRightIcon,
              text: 'Normal',
              boxContainerStyles: { ...basicLinkStyles },
            },
            {
              subMenu: true,
              to: '/addActivation?type=product_launch',
              icon: KeyboardArrowRightIcon,
              text: 'Product Launch',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
      ...(Boolean(
        ApiProvider.permission('activation_create') &&
        !ApiProvider.permission('activation_create_product_launch'),
      )
        ? [
            {
              to: '/addActivity',
              icon: ActivityCreateIcon,
              text: 'Activity',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
      ...(Boolean(
        !ApiProvider.permission('activation_create') &&
        ApiProvider.permission('activation_create_product_launch'),
      )
        ? [
            {
              to: '/addActivation?type=product_launch',
              icon: ActivityCreateIcon,
              text: 'Activity',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
      ...(Boolean(ApiProvider.permission('brand_tracker_create'))
        ? [
            {
              to: '/addbrand',
              icon: BrandCreateIcon,
              text: 'Brand',
              boxContainerStyles: { ...basicLinkStyles },
            },
          ]
        : []),
    ],
    [userCan, property_id, brand_id],
  );

  const allViewLinks = useMemo(
    () => [...links.visible, ...links.hidden],
    [links.visible, links.hidden],
  );
  const hasAnyViewsAccess = allViewLinks.length > 0;
  const hasAnyCreateAccess = viewsAddLinks.length > 0;

  if (!hasAnyViewsAccess && !hasAnyCreateAccess) {
    return null;
  }

  return (
    <Box>
      <Typography
        sx={{
          color: '#DBDBDB',
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: 'normal',
          textTransform: 'uppercase',
          mt: '15px',
        }}
      >
        Views
      </Typography>

      {!compactSidebarMenus && links?.visible.length ? (
        <>
          {links.visible.map(({ to, icon, text, target, handleClick }, index) => {
            return (
              <SidebarLink
                key={index}
                to={to}
                icon={icon}
                text={text}
                boxContainerStyles={basicLinkStyles}
                target={target}
                // @ts-expect-error Click call.
                handleClick={() => handleClick && handleClick()}
              />
            );
          })}
        </>
      ) : null}

      {!compactSidebarMenus && links?.hidden.length ? (
        <SidebarLinkSet
          id={isSectionOpen('moreViews') ? 'more-views-popper' : undefined}
          title='More views'
          icon={ThreeDotsIcon}
          links={links.hidden}
          open={isSectionOpen('moreViews')}
          anchorEl={anchorEls.moreViews}
          onTriggerClick={handleClick('moreViews')}
        />
      ) : null}

      {compactSidebarMenus && hasAnyViewsAccess ? (
        <SidebarLinkSet
          id={isSectionOpen('moreViews') ? 'list-views-popper' : undefined}
          title='List views'
          icon={ThreeDotsIcon}
          links={allViewLinks}
          open={isSectionOpen('moreViews')}
          anchorEl={anchorEls.moreViews}
          onTriggerClick={handleClick('moreViews')}
        />
      ) : null}

      {hasAnyCreateAccess && (
        <SidebarLinkSet
          id={isSectionOpen('createNew') ? 'create-new-popper' : undefined}
          title='Create new'
          icon={compactSidebarMenus ? ThreeDotsIcon : PlusIcon}
          links={viewsAddLinks}
          open={isSectionOpen('createNew')}
          anchorEl={anchorEls.createNew}
          onTriggerClick={handleClick('createNew')}
        />
      )}
    </Box>
  );
};

export default SidebarViews;
